import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { QuoteTemplate } from '@/types/quote';
import { ArrowLeft, Upload, FileText, Loader2, Save, Layers, Eye, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuoteTemplates } from '@/hooks/useQuoteTemplates';
import { StandardTermsSelector } from '@/components/crm/quote/StandardTermsSelector';
import { useTemplateTerms } from '@/hooks/useStandardTerms';
import { getDocument } from 'pdfjs-dist';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

export default function CRMQuoteTemplateEdit() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const isNew = templateId === 'new';
  
  const { templates, loading: templatesLoading, createTemplate, updateTemplate } = useQuoteTemplates();
  const existingTemplate = templates.find(t => t.id === templateId);
  
  const [formData, setFormData] = useState<Partial<QuoteTemplate>>({
    name: '',
    description: '',
    template_body: '',
    template_terms: '',
    header_logo_url: '',
    background_image_url: '',
    brand_primary: '#014336',
    brand_secondary: '#10B981',
    footer_text: '',
    is_default: false,
    pdf_template_url: null,
    pdf_page_count: null,
    placeholder_positions: null,
    page_count: 1,
    page_backgrounds: null,
  });
  
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const initialDataRef = useRef<string | null>(null);

  // Load template data
  useEffect(() => {
    if (existingTemplate) {
      setFormData(existingTemplate);
      initialDataRef.current = JSON.stringify(existingTemplate);
    } else if (isNew) {
      initialDataRef.current = JSON.stringify(formData);
    }
  }, [existingTemplate, isNew]);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (initialDataRef.current === null) return false;
    return JSON.stringify(formData) !== initialDataRef.current;
  }, [formData]);

  const handleUploadImage = async (file: File, type: 'logo' | 'background') => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `quote-templates/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      setFormData(prev => ({
        ...prev,
        [type === 'logo' ? 'header_logo_url' : 'background_image_url']: publicUrl
      }));

      toast.success('Afbeelding geüpload');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Fout bij uploaden afbeelding', { description: error.message });
    } finally {
      setUploading(false);
    }
  };

  const handlePDFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Upload een geldig PDF bestand');
      return;
    }

    setUploading(true);
    try {
      const fileExt = 'pdf';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `templates/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('quote-templates')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('quote-templates')
        .getPublicUrl(filePath);

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;

      const updatedData = {
        ...formData,
        pdf_template_url: publicUrl,
        pdf_page_count: pageCount,
        page_count: Math.max(formData.page_count || 1, pageCount),
      };

      setFormData(updatedData);

      if (!isNew && templateId) {
        setSaving(true);
        await updateTemplate(templateId, updatedData);
        initialDataRef.current = JSON.stringify(updatedData);
        setSaving(false);
      }

      toast.success(`PDF geüpload (${pageCount} pagina's)`);
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('PDF upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleTermsUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Upload een geldig PDF bestand');
      return;
    }

    setUploading(true);
    try {
      const fileName = `${Date.now()}-terms-${Math.random().toString(36).substring(7)}.pdf`;
      const filePath = `templates/terms/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('quote-templates')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('quote-templates')
        .getPublicUrl(filePath);

      const updatedData = { ...formData, terms_document_url: publicUrl };
      setFormData(updatedData);

      if (!isNew && templateId) {
        setSaving(true);
        await updateTemplate(templateId, updatedData);
        initialDataRef.current = JSON.stringify(updatedData);
        setSaving(false);
        toast.success('Algemene voorwaarden PDF geüpload en opgeslagen');
      } else {
        toast.success('Algemene voorwaarden PDF geüpload');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('PDF upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Naam is verplicht');
      return;
    }

    try {
      setSaving(true);
      if (isNew) {
        const newTemplate = await createTemplate(formData);
        initialDataRef.current = JSON.stringify(formData);
        navigate(`/crm/quote-templates/${newTemplate.id}`, { replace: true });
      } else if (templateId) {
        await updateTemplate(templateId, formData);
        initialDataRef.current = JSON.stringify(formData);
      }
    } catch (error) {
      // Error handled in hook
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (!confirm('Je hebt onopgeslagen wijzigingen. Weet je zeker dat je wilt teruggaan?')) {
        return;
      }
    }
    navigate('/crm/quote-templates');
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData]);

  if (templatesLoading && !isNew) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => navigate('/crm/quotes')} className="cursor-pointer">
                      Offertes
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => navigate('/crm/quote-templates')} className="cursor-pointer">
                      Templates
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{isNew ? 'Nieuw Template' : formData.name || 'Template'}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <h1 className="text-2xl font-bold mt-1">
                {isNew ? 'Nieuw Template' : formData.name || 'Template'}
              </h1>
            </div>
            {hasUnsavedChanges && (
              <Badge variant="outline" className="text-orange-600 border-orange-600">
                Niet opgeslagen
              </Badge>
            )}
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (formData.pdf_template_url || (formData.page_count && formData.page_count > 0)) && (
              <Button
                variant="outline"
                onClick={() => navigate(`/crm/quote-templates/${templateId}/design`)}
              >
                <Layers className="h-4 w-4 mr-2" />
                Placeholder Designer
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="basic">Basis Info</TabsTrigger>
              <TabsTrigger value="pdf">PDF Template</TabsTrigger>
              <TabsTrigger value="terms">Voorwaarden</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Algemene Informatie</CardTitle>
                  <CardDescription>Basisgegevens van het template</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Naam *</Label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Bijv. Standaard Offerte 2026"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Beschrijving</Label>
                    <Textarea
                      id="description"
                      value={formData.description || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      placeholder="Korte beschrijving van dit template"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_default"
                      checked={formData.is_default || false}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                    />
                    <Label htmlFor="is_default">Standaard template voor nieuwe offertes</Label>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Branding</CardTitle>
                  <CardDescription>Visuele identiteit voor dit template</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Header Logo</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.header_logo_url || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, header_logo_url: e.target.value }))}
                        placeholder="URL of upload een afbeelding"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploading}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) handleUploadImage(file, 'logo');
                          };
                          input.click();
                        }}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                    {formData.header_logo_url && (
                      <img src={formData.header_logo_url} alt="Logo preview" className="h-16 object-contain" />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="brand_primary">Primaire Kleur</Label>
                      <div className="flex gap-2">
                        <Input
                          id="brand_primary"
                          type="color"
                          value={formData.brand_primary || '#014336'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_primary: e.target.value }))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={formData.brand_primary || '#014336'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_primary: e.target.value }))}
                          placeholder="#014336"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="brand_secondary">Secundaire Kleur</Label>
                      <div className="flex gap-2">
                        <Input
                          id="brand_secondary"
                          type="color"
                          value={formData.brand_secondary || '#10B981'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_secondary: e.target.value }))}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          value={formData.brand_secondary || '#10B981'}
                          onChange={(e) => setFormData(prev => ({ ...prev, brand_secondary: e.target.value }))}
                          placeholder="#10B981"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="footer_text">Footer Tekst</Label>
                    <Textarea
                      id="footer_text"
                      value={formData.footer_text || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, footer_text: e.target.value }))}
                      rows={2}
                      placeholder="Bijv. Bedrijfsnaam | KvK: 12345678 | BTW: NL123456789B01"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pdf" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>PDF Template</CardTitle>
                  <CardDescription>
                    Upload een PDF als achtergrond voor je offerte. Na uploaden kun je placeholders positioneren in de Designer.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    type="file"
                    accept="application/pdf"
                    onChange={handlePDFUpload}
                    disabled={uploading}
                  />

                  {formData.pdf_template_url && (
                    <div className="p-4 border rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-6 w-6 text-primary" />
                          <div>
                            <p className="font-medium">PDF Template geüpload</p>
                            <p className="text-sm text-muted-foreground">
                              {formData.pdf_page_count} pagina('s)
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(formData.pdf_template_url!, '_blank')}
                          >
                            <Eye className="h-4 w-4 mr-1.5" />
                            Bekijken
                          </Button>
                          {!isNew && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => navigate(`/crm/quote-templates/${templateId}/design`)}
                            >
                              <Layers className="h-4 w-4 mr-1.5" />
                              Naar Designer
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {uploading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Algemene Voorwaarden (PDF)</CardTitle>
                  <CardDescription>
                    Upload je algemene voorwaarden als PDF - deze worden automatisch als laatste pagina's toegevoegd aan de offerte
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    type="file"
                    accept="application/pdf"
                    onChange={handleTermsUpload}
                    disabled={uploading}
                  />

                  {formData.terms_document_url && (
                    <div className="p-4 border rounded-lg bg-emerald-500/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-6 w-6 text-emerald-600" />
                          <div>
                            <p className="font-medium">Voorwaarden PDF geüpload</p>
                            <p className="text-sm text-muted-foreground">
                              Wordt als laatste pagina's toegevoegd
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(formData.terms_document_url!, '_blank')}
                          >
                            Bekijken
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData(prev => ({ ...prev, terms_document_url: null }))}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="terms" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Standaard Voorwaarden</CardTitle>
                  <CardDescription>
                    Selecteer voorwaarden uit de bibliotheek die standaard worden toegepast op offertes met dit template
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!isNew && templateId ? (
                    <StandardTermsSelectorWrapper templateId={templateId} />
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      Sla het template eerst op om voorwaarden te kunnen selecteren.
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// Wrapper for StandardTermsSelector with hooks
function StandardTermsSelectorWrapper({ templateId }: { templateId: string }) {
  const { selectedTermIds, isLoading, setSelectedTerms } = useTemplateTerms(templateId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <StandardTermsSelector
      selectedIds={selectedTermIds}
      onChange={setSelectedTerms}
    />
  );
}
